import React, {createContext, ReactNode, use, useEffect, useReducer} from "react";
import {GeoPosition} from "../models/search_query";

export type GeoPositionStatus = 'idle' | 'pending' | 'resolved' | 'rejected';

export interface GeoPositionState {
    status: GeoPositionStatus;
    position: GeoPosition | null;
    error: Error | null;
}

type GeoPositionAction =
    | { type: 'error'; error: Error }
    | { type: 'success'; position: GeolocationPosition }
    | { type: 'started' };

function geoPositionReducer(state: GeoPositionState, action: GeoPositionAction): GeoPositionState {
    switch (action.type) {
        case 'error': {
            return {
                ...state,
                status: 'rejected',
                error: action.error,
            }
        }
        case 'success': {
            const lat = action.position.coords.latitude;
            const lng = action.position.coords.longitude;
            const position: GeoPosition = { lat, lng };
            if (position.lat != state.position?.lat || position.lng != state.position?.lng) {
                return {
                    ...state,
                    status: 'resolved',
                    position: position,
                }
            } else {
                return state
            }
        }
        case 'started': {
            return {
                ...state,
                status: 'pending',
            }
        }
        default: {
            throw new Error(`Unhandled action type: ${(action as any).type}`)
        }
    }
}

const GeoPositionContext = createContext<GeoPositionState | null>(null);

export function GeoPositionProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(geoPositionReducer, {
        status: 'idle',
        position: null,
        error: null,
    } as GeoPositionState)

    useEffect(() => {
        if (!navigator.geolocation) {
            dispatch({
                type: 'error',
                error: new Error('Geolocation is not supported'),
            })
            return
        }
        dispatch({ type: 'started' })
        navigator.geolocation.getCurrentPosition(
            (position) => dispatch({ type: 'success', position }),
            (error) => dispatch({ type: 'error', error: new Error(error.message) }),
        );
    }, []);

    return (
        <GeoPositionContext value={state}>
            {children}
        </GeoPositionContext>
    );
}

export function useGeoPosition() {
    const context = use(GeoPositionContext);
    if (!context) {
        throw new Error('useGeoLocation must be used within a GeoPositionProvider')
    }
    return context;
}
